/**
 * Where the admin "View as Other User" target lives between page loads.
 *
 * localStorage, so a refresh — or opening a link in a new tab — keeps the admin on the user
 * they were looking at. It used to be in-memory only, which meant every reload silently
 * dropped back to the admin's own view mid-investigation.
 *
 * The value is stored base64-encoded rather than as plain logins. That is obfuscation, not
 * protection: anyone with devtools can decode it, and nothing here is a secret — the server
 * re-authorizes every impersonated request against the real caller. What it buys is that the
 * value does not read as a login to someone glancing at storage, and that it is not picked up
 * verbatim by tools that scan storage for aliases.
 *
 * It records WHO set it as well as who they are viewing as — `base64("<owner>:<target>")`. The
 * key is shared by everyone who signs in on this browser, so a target alone would be inherited:
 * admin A views as X and signs out without exiting, admin B signs in on the same machine and
 * opens the portal already viewing as X, without having chosen to. A value whose owner is not
 * the signed-in user is therefore not theirs, and is removed when read.
 *
 * Every read is defensive, because the stored value is outside this code's control: a user can
 * edit it, an older build may have written something else, and Safari in private mode throws on
 * access. Anything that does not decode to a well-formed alias is treated as "not
 * impersonating" and removed, so a bad value cannot strand an admin in a view they cannot exit.
 */

/** The storage key. Namespaced to the portal, since several modules share one origin. */
export const IMPERSONATION_STORAGE_KEY = 'rocky:viewAs';

/**
 * The group that may use View as. Exported so the context and the Event Hub request middleware
 * — which derives the impersonation header itself, outside React — gate on the same thing.
 */
export const VIEW_AS_ADMIN_GROUP = 'rocky-admin';

/**
 * What a login may look like. The strictest of the services that consume it — this is the
 * Event Hub server's own check (auth/Impersonation.java), and the Gateway accepts a superset.
 * Validating on write AND read means a value that would be refused downstream never gets as
 * far as a request.
 */
const ALIAS = /^[a-z0-9._-]{1,64}$/;

/** A login, normalised the way the context stores it: trimmed and lower-cased. */
export function normaliseAlias(alias: string): string {
  return alias.trim().toLowerCase();
}

export function isValidAlias(alias: string): boolean {
  return ALIAS.test(alias);
}

/** Who set the view, and who they are viewing as. */
export interface StoredImpersonation {
  owner: string;
  target: string;
}

/**
 * base64 of `owner:target`. The colon cannot appear in either login (see the pattern above), so
 * it splits unambiguously; logins are ASCII by the same pattern, so `btoa` is safe.
 */
export function encodeImpersonation(value: StoredImpersonation): string {
  return btoa(`${value.owner}:${value.target}`);
}

/** What a stored value decodes to, or null when it is not a well-formed owner/target pair. */
export function decodeImpersonation(stored: string): StoredImpersonation | null {
  let decoded: string;
  try {
    decoded = atob(stored);
  } catch {
    // Not base64 at all — `atob` throws InvalidCharacterError.
    return null;
  }
  const parts = decoded.split(':');
  if (parts.length !== 2) return null;
  const owner = normaliseAlias(parts[0]!);
  const target = normaliseAlias(parts[1]!);
  return isValidAlias(owner) && isValidAlias(target) ? { owner, target } : null;
}

/**
 * The target `owner` stored, or null.
 *
 * Anything else present under the key is removed on the way out rather than left to be re-read
 * on every load: a value that does not decode, and a value set by someone other than `owner` —
 * the previous person to sign in on this browser, whose view this user did not choose.
 */
export function loadImpersonation(owner: string): string | null {
  try {
    const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!stored) return null;
    const value = decodeImpersonation(stored);
    if (!value || value.owner !== normaliseAlias(owner)) {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      return null;
    }
    return value.target;
  } catch {
    // localStorage unavailable. Not impersonating is always a safe answer.
    return null;
  }
}

/**
 * Stores `owner`'s target, or clears it when given null.
 *
 * A failed write is swallowed: the view still switches for this page, and the only thing lost
 * is that a reload will not remember it. An invalid login is refused rather than stored, so the
 * next load cannot pick up something the services would reject.
 */
export function saveImpersonation(owner: string, target: string | null): void {
  try {
    if (target === null) {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      return;
    }
    const value = { owner: normaliseAlias(owner), target: normaliseAlias(target) };
    if (!isValidAlias(value.owner) || !isValidAlias(value.target)) return;
    localStorage.setItem(IMPERSONATION_STORAGE_KEY, encodeImpersonation(value));
  } catch {
    // Ignored on purpose — see above.
  }
}
